import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import type { Moment } from 'moment';
import Notification from '@this/src/notification';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import type { EanHotelCancelInventoryItemResponseArgs } from '@this/src/domain/arrangement/ean_hotel_cancel_inventory_item';
import EanHotelCancelInventoryItem from '@this/src/domain/arrangement/ean_hotel_cancel_inventory_item';
import useOrganizationSelector from '../../../use_organization_selector';
import CancelSearchArea from './cancel_search_area';
import CancelResultTable from './cancel_result_table';
import type { InventoryStatus, SortKey, SortDirection } from './types';

interface Response {
  cancel_items: EanHotelCancelInventoryItemResponseArgs[];
  total_count: number;
  total_page: number;
}

const Cancel = observer(() => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<EanHotelCancelInventoryItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPage, setTotalPage] = useState(1);
  const [page, setPage] = useState(parseInt(utils.getParam('page') || '1', 10));

  const [inventoryStatus, setInventoryStatus] = useState<InventoryStatus>(
    (utils.getParam('inventory_status') as InventoryStatus) || 'not_yet'
  );
  const [tripId, setTripId] = useState<number | undefined>(utils.getParamInt('trip_id') ?? undefined);
  // デフォルトは全期間: API失敗行（要手動対応・損金リスク）を日付絞り込みで取りこぼさないため
  const [requestedDateFrom, setRequestedDateFrom] = useState<Moment | undefined>(
    utils.getParamMoment('requested_date_from') ?? undefined
  );
  const [requestedDateTo, setRequestedDateTo] = useState<Moment | undefined>(
    utils.getParamMoment('requested_date_to') ?? undefined
  );
  const [organizationIds, renderOrganizationSelector] = useOrganizationSelector();
  const [sortKey, setSortKey] = useState<SortKey>((utils.getParam('sort_key') as SortKey) || 'requested_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    (utils.getParam('sort_direction') as SortDirection) || 'asc'
  );
  const [isPaginate, setIsPaginate] = useState(true);
  // 検索条件の連続変更で古いレスポンスが最新結果を上書きしないよう、最新リクエストのみ反映する
  const requestIdRef = useRef(0);

  const fetchItems = useCallback(() => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsLoading(true);
    setItems([]);
    setTotalCount(0);
    setTotalPage(1);

    const params = {
      page,
      inventory_status: inventoryStatus,
      trip_id: tripId,
      requested_date_from: requestedDateFrom?.format('YYYY-MM-DD'),
      requested_date_to: requestedDateTo?.format('YYYY-MM-DD'),
      organization_ids: organizationIds.length > 0 ? organizationIds : undefined,
      sort_key: sortKey,
      sort_direction: sortDirection,
      pagination: isPaginate ? 'true' : 'false'
    };
    _.each(params, (value, key) => {
      if (value === undefined) {
        delete params[key as keyof typeof params];
      }
    });

    // URL同期はorganization_idsをカンマ結合にする（useOrganizationSelectorの復元形式に合わせる）
    const urlParams: Record<string, string | number> = {};
    _.each(params, (value, key) => {
      if (value === undefined) return;
      urlParams[key] = _.isArray(value) ? value.join(',') : value;
    });
    const query = _.map(urlParams, (value, key) => `${key}=${value}`).join('&');
    history.push(
      `/arrangement/todo_list?tab=AUTO_RESERVATION_INVENTORY&sub_tab=EAN_HOTEL&ean_hotel_flow=CANCEL&ean_hotel_type=ITEMS&${query}`
    );

    Fetcher.get<Response>('/arrangement/auto_reservation_inventory/ean_hotel_cancel/items.json', params)
      .then(res => {
        if (requestId !== requestIdRef.current) return;

        setItems(res.cancel_items.map(args => EanHotelCancelInventoryItem.fromResponse(args)));
        setTotalCount(res.total_count);
        setTotalPage(res.total_page);
        if (res.total_page < page) {
          setPage(1);
        }
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;

        Notification.error('データの取得に失敗しました');
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;

        setIsLoading(false);
      });
  }, [
    page,
    inventoryStatus,
    tripId,
    requestedDateFrom,
    requestedDateTo,
    organizationIds,
    sortKey,
    sortDirection,
    isPaginate,
    history
  ]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return (
    <>
      <CancelSearchArea
        inventoryStatus={inventoryStatus}
        tripId={tripId}
        requestedDateFrom={requestedDateFrom}
        requestedDateTo={requestedDateTo}
        sortKey={sortKey}
        sortDirection={sortDirection}
        isPaginate={isPaginate}
        setInventoryStatus={setInventoryStatus}
        setTripId={setTripId}
        setRequestedDateFrom={setRequestedDateFrom}
        setRequestedDateTo={setRequestedDateTo}
        setSortKey={setSortKey}
        setSortDirection={setSortDirection}
        setIsPaginate={setIsPaginate}
        renderOrganizationSelector={renderOrganizationSelector}
      />
      {!isLoading && !isPaginate && totalCount > items.length && (
        <Alert variant="warning" style={{ marginBottom: '10px' }}>
          {`全件表示は先頭${items.length.toLocaleString()}件までです。絞り込み条件を指定してください`}
        </Alert>
      )}
      {isLoading ? (
        <Loading />
      ) : (
        <CancelResultTable
          items={items}
          totalCount={totalCount}
          totalPage={totalPage}
          page={page}
          fetchItems={fetchItems}
          setPage={setPage}
        />
      )}
    </>
  );
});

export default Cancel;
